"use client";

import {
  PackageOpen,
  ArrowRight,
  ArrowLeft,
  Mail,
  Package,
  Check,
  UserRound,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { GmailIcon, OutlookIcon } from "@/components/Icons";
import { motion } from "framer-motion";

interface HowItWorksStepProps {
  onNext: () => void;
  onBack: () => void;
}

export const StepLayout = ({
  children,
  onNext,
  onBack,
  nextLabel = "Next",
  nextIcon: NextIcon = ArrowRight,
  disabled = false,
  showNext = true,
  showBack = true,
  nextVariant = "default",
  centerButtons = false,
}: any) => {
  const hasButtons = showNext || showBack;

  return (
    <div className="flex flex-col items-center flex-1 text-center w-full pt-2 min-h-0">
      <div className="flex-1 flex flex-col items-center justify-start w-full overflow-y-auto min-h-0">
        {children}
      </div>
      {hasButtons && (
        <div
          className={cn(
            "flex items-center w-full flex-shrink-0",
            centerButtons
              ? "justify-center gap-6 pt-8 pb-6"
              : "justify-between pb-1 pt-2"
          )}
        >
          {centerButtons ? (
            <>
              {showBack && (
                <Button
                  variant="ghost"
                  onClick={onBack}
                  className="h-8 px-3 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </Button>
              )}
              {showNext && (
                <Button
                  variant={nextVariant as any}
                  onClick={onNext}
                  disabled={disabled}
                  className="text-sm"
                >
                  <NextIcon className="w-4 h-4" />
                  {nextLabel}
                </Button>
              )}
            </>
          ) : (
            <>
              <div className="flex-1 flex justify-start">
                {showBack && (
                  <Button variant="ghost" onClick={onBack}>
                    <ArrowLeft className="w-4 h-4" /> Back
                  </Button>
                )}
              </div>
              <div className="flex-1 flex justify-end px-2">
                {showNext && (
                  <Button
                    variant={nextVariant as any}
                    onClick={onNext}
                    disabled={disabled}
                  >
                    <NextIcon />
                    {nextLabel}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const InboxOrbitAnimation = () => {
  const CENTER_X = 112;
  const CENTER_Y = 118.2812;
  const ORBIT_DURATION = 30;

  return (
    <svg
      width="224"
      height="230"
      viewBox="0 0 224 230"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="scale-110"
    >
      <defs>
        <filter
          id="filter0_d_inbox"
          x="40.25"
          y="47.2812"
          width="144"
          height="144"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_inbox"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_inbox"
            result="shape"
          />
        </filter>
      </defs>

      <motion.g
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "center" }}
      >
        <g filter="url(#filter0_d_inbox)">
          <rect
            x="42.25"
            y="48.2812"
            width="140"
            height="140"
            rx="70"
            fill="white"
          />
          <rect
            x="42.75"
            y="48.7812"
            width="139"
            height="139"
            rx="69.5"
            stroke="#EBEDF0"
          />
          <motion.path
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.2,
              type: "spring",
              stiffness: 80,
              damping: 15,
            }}
            style={{ originX: "112px", originY: "118px" }}
            d="M112.25 138.28V120.28M128.25 120.28V128.02C128.251 128.775 128.044 129.515 127.653 130.161C127.261 130.806 126.7 131.332 126.03 131.68L114.03 137.84C113.48 138.126 112.87 138.275 112.25 138.275C111.63 138.275 111.02 138.126 110.47 137.84L98.47 131.68C97.8002 131.332 97.2389 130.806 96.8474 130.161C96.4559 129.515 96.2492 128.775 96.25 128.02V120.28M118.59 98.6996C119.088 98.4211 119.649 98.2749 120.22 98.2749C120.791 98.2749 121.352 98.4211 121.85 98.6996L130.25 103.42C130.845 103.756 131.34 104.244 131.684 104.835C132.029 105.425 132.21 106.096 132.21 106.78C132.21 107.463 132.029 108.134 131.684 108.725C131.34 109.315 130.845 109.803 130.25 110.14L105.89 123.86C105.39 124.145 104.825 124.294 104.25 124.294C103.675 124.294 103.11 124.145 102.61 123.86L94.25 119.14C93.6551 118.803 93.1602 118.315 92.8159 117.725C92.4715 117.134 92.29 116.463 92.29 115.78C92.29 115.096 92.4715 114.425 92.8159 113.835C93.1602 113.244 93.6551 112.756 94.25 112.42L118.59 98.6996ZM130.25 119.14C130.845 118.803 131.34 118.315 131.684 117.725C132.029 117.134 132.21 116.463 132.21 115.78C132.21 115.096 132.029 114.425 131.684 113.835C131.34 113.245 130.845 112.756 130.25 112.42L105.91 98.6798C105.414 98.3956 104.852 98.2461 104.28 98.2461C103.708 98.2461 103.146 98.3956 102.65 98.6798L94.25 103.42C93.6551 103.756 93.1602 104.245 92.8159 104.835C92.4715 105.425 92.29 106.096 92.29 106.78C92.29 107.463 92.4715 108.134 92.8159 108.725C93.1602 109.315 93.6551 109.803 94.25 110.14L118.61 123.86C119.106 124.145 119.668 124.295 120.24 124.295C120.812 124.295 121.374 124.145 121.87 123.86L130.25 119.14Z"
            stroke="#1E1E1E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </motion.g>

      {/* Orbit group */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <motion.g
          animate={{ rotate: 360 }}
          transition={{
            duration: ORBIT_DURATION,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            transformBox: "fill-box",
            transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
          }}
        >
          {/* Gmail satellite */}
          <g>
            <circle
              cx="64.25"
              cy="63.7812"
              r="23.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <g transform="translate(50.75 55.1562)">
                <GmailIcon size={27} />
              </g>
            </motion.g>
          </g>

          {/* Outlook satellite */}
          <g>
            <circle
              cx="159.75"
              cy="166.219"
              r="23.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <g transform="translate(144.75 152.344)">
                <OutlookIcon size={30} />
              </g>
            </motion.g>
          </g>
        </motion.g>
      </motion.g>
    </svg>
  );
};

export const FilteringOrbitAnimation = () => {
  const CENTER_X = 112.375;
  const CENTER_Y = 115;
  const ORBIT_DURATION = 30;

  return (
    <svg
      width="224"
      height="230"
      viewBox="0 0 224 230"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="scale-110"
    >
      <defs>
        <filter
          id="filter0_d_step2"
          x="40.375"
          y="44"
          width="144"
          height="144"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="1" />
          <feGaussianBlur stdDeviation="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_step2"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_step2"
            result="shape"
          />
        </filter>
      </defs>

      <motion.g
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformOrigin: "center" }}
      >
        <g filter="url(#filter0_d_step2)">
          <rect
            x="42.375"
            y="45"
            width="140"
            height="140"
            rx="70"
            fill="white"
          />
          <rect
            x="42.875"
            y="45.5"
            width="139"
            height="139"
            rx="69.5"
            stroke="#EBEDF0"
          />
          <motion.path
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.2,
              type: "spring",
              stiffness: 80,
              damping: 15,
            }}
            style={{ originX: "112px", originY: "115px" }}
            d="M103.375 99.54L121.375 109.84M94.9751 105L112.375 115M112.375 115L129.775 105M112.375 115L112.375 135M130.375 107C130.374 106.298 130.189 105.609 129.838 105.002C129.487 104.395 128.982 103.891 128.375 103.54L114.375 95.5398C113.767 95.1887 113.077 95.0039 112.375 95.0039C111.673 95.0039 110.983 95.1887 110.375 95.5398L96.375 103.54C95.7675 103.891 95.263 104.395 94.9119 105.002C94.5609 105.609 94.3757 106.298 94.375 107V123C94.3757 123.701 94.5609 124.39 94.9119 124.997C95.263 125.605 95.7675 126.109 96.375 126.46L110.375 134.46C110.983 134.811 111.673 134.996 112.375 134.996C113.077 134.996 113.767 134.811 114.375 134.46L128.375 126.46C128.982 126.109 129.487 125.605 129.838 124.997C130.189 124.39 130.374 123.701 130.375 123V107Z"
            stroke="#1E1E1E"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </motion.g>

      <motion.g
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
      >
        <motion.g
          animate={{ rotate: 360 }}
          transition={{
            duration: ORBIT_DURATION,
            repeat: Infinity,
            ease: "linear",
          }}
          style={{
            transformBox: "fill-box",
            transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
          }}
        >
          {/* Satellite 1 - Right */}
          <g>
            <circle
              cx="179.875"
              cy="115"
              r="19.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <path
                d="M188.208 110.832L180.733 115.582C180.476 115.743 180.179 115.829 179.875 115.829C179.571 115.829 179.274 115.743 179.017 115.582L171.542 110.832M173.208 108.332H186.542C187.462 108.332 188.208 109.078 188.208 109.999V119.999C188.208 120.919 187.462 121.665 186.542 121.665H173.208C172.288 121.665 171.542 120.919 171.542 119.999V109.999C171.542 109.078 172.288 108.332 173.208 108.332Z"
                stroke="#C1C4C9"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.g>
          </g>

          {/* Satellite 2 - Top */}
          <g>
            <circle
              cx="112.125"
              cy="45"
              r="19.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <path
                d="M120.458 40.832L112.983 45.582C112.726 45.7432 112.429 45.8287 112.125 45.8287C111.821 45.8287 111.524 45.7432 111.267 45.582L103.792 40.832M105.458 38.332H118.792C119.712 38.332 120.458 39.0782 120.458 39.9987V49.9987C120.458 50.9192 119.712 51.6654 118.792 51.6654H105.458C104.538 51.6654 103.792 50.9192 103.792 49.9987V39.9987C103.792 39.0782 104.538 38.332 105.458 38.332Z"
                stroke="#C1C4C9"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.g>
          </g>

          {/* Satellite 3 - Left */}
          <g>
            <circle
              cx="44.125"
              cy="115"
              r="19.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <path
                d="M52.4584 110.832L44.9834 115.582C44.7261 115.743 44.4287 115.829 44.1251 115.829C43.8215 115.829 43.524 115.743 43.2667 115.582L35.7917 110.832M37.4584 108.332H50.7917C51.7122 108.332 52.4584 109.078 52.4584 109.999V119.999C52.4584 120.919 51.7122 121.665 50.7917 121.665H37.4584C36.5379 121.665 35.7917 120.919 35.7917 119.999V109.999C35.7917 109.078 36.5379 108.332 37.4584 108.332Z"
                stroke="#C1C4C9"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.g>
          </g>

          {/* Satellite 4 - Bottom */}
          <g>
            <circle
              cx="113.125"
              cy="185"
              r="19.5"
              fill="white"
              stroke="#EBEDF0"
            />
            <motion.g
              animate={{ rotate: -360 }}
              transition={{
                duration: ORBIT_DURATION,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                transformBox: "fill-box",
                transformOrigin: `${CENTER_X}px ${CENTER_Y}px`,
              }}
            >
              <path
                d="M121.458 180.832L113.983 185.582C113.726 185.743 113.429 185.829 113.125 185.829C112.821 185.829 112.524 185.743 112.267 185.582L104.792 180.832M106.458 178.332H119.792C120.712 178.332 121.458 179.078 121.458 179.999V189.999C121.458 190.919 120.712 191.665 119.792 191.665H106.458C105.538 191.665 104.792 190.919 104.792 189.999V179.999C104.792 179.078 105.538 178.332 106.458 178.332Z"
                stroke="#C1C4C9"
                strokeWidth="1.25"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </motion.g>
          </g>
        </motion.g>
      </motion.g>
    </svg>
  );
};

const WorkflowAnimation = () => {
  return (
    <div className="relative w-64 h-48 flex items-center justify-center">
      <div className="relative -ml-32">
        {/* Main Outer Ball */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="w-35 h-35 border border-border rounded-full flex items-center justify-center bg-white shadow-sm relative z-10"
        >
          {/* Package Icon (Inner Ball) */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 0.5,
              delay: 0.2,
              type: "spring",
              stiffness: 80,
              damping: 15,
            }}
          >
            <Package size={48} strokeWidth={1} />
          </motion.div>

          {/* Mail Satellite */}
          <motion.div
            initial={{ scale: 0, opacity: 0, x: -10 }}
            animate={{ scale: 1, opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.5, ease: "easeOut" }}
            className="absolute top-1/2 -translate-y-1/2 -right-5 z-20"
          >
            <div className="w-10 h-10 bg-white border border-muted-foreground rounded-full flex items-center justify-center shadow-sm">
              <Mail
                size={20}
                className="text-muted-foreground"
                strokeWidth={1.5}
              />
            </div>
          </motion.div>
        </motion.div>

        {/* Top Path (Green - Closed) */}
        <div className="absolute left-40 top-8 w-20 h-10">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 40"
            fill="none"
            className="overflow-visible"
          >
            <motion.path
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 0.8, ease: "easeInOut" }}
              d="M0 43.3125C5.46778 43.3125 10.7788 41.4854 15.0893 38.1214L51.7114 9.54136C59.6099 3.37728 69.4238 0.18565 79.4372 0.524486"
              stroke="#15803D"
              strokeWidth="1.5"
            />
          </svg>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 1.6 }}
            className="absolute left-15 -top-1.5 bg-white border border-green-700 rounded-sm px-1.5 py-0.5 flex items-center gap-1 shadow-sm whitespace-nowrap"
          >
            <Check size={16} strokeWidth={2} className="text-green-700" />
            <span className="text-xs font-semibold text-green-700 pl-1">
              Closed
            </span>
          </motion.div>
        </div>

        {/* Bottom Path (Yellow - Task) */}
        <div className="absolute left-40 bottom-8.5 w-20 h-10">
          <svg
            width="100%"
            height="100%"
            viewBox="0 0 100 40"
            fill="none"
            className="overflow-visible"
          >
            <motion.path
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.8, delay: 1.0, ease: "easeInOut" }}
              d="M0 0.5C5.46778 0.5 10.7788 2.3271 15.0893 5.69105L51.7114 34.2711C59.6099 40.4352 69.4238 43.6269 79.4372 43.288"
              stroke="#A16207"
              strokeWidth="1.5"
            />
          </svg>
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -10 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 1.8 }}
            className="absolute left-15 -bottom-2.5 bg-white border border-yellow-700 rounded-sm px-1.5 py-0.5 flex items-center gap-1 shadow-sm whitespace-nowrap"
          >
            <UserRound size={16} strokeWidth={2} className="text-yellow-700" />
            <span className="text-xs font-semibold text-yellow-700 pl-1">
              Task
            </span>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export const HowItWorksStep1 = (props: HowItWorksStepProps) => (
  <StepLayout
    {...props}
    centerButtons={true}
    showBack={false}
    nextVariant="outline"
  >
    <div className="relative w-64 h-64 flex items-center justify-center">
      <InboxOrbitAnimation />
    </div>
    <div className="space-y-2 max-w-lg px-4 mt-8">
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="text-lg font-medium"
      >
        1. Connect channels to specialized inboxes
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="text-sm text-muted-foreground"
      >
        Configure distinct Inboxes for specific tasks - like Billing, Returns,
        or Onboarding - where AI agents do the work
      </motion.p>
    </div>
  </StepLayout>
);

export const HowItWorksStep2 = (props: HowItWorksStepProps) => (
  <StepLayout {...props} centerButtons={true} nextVariant="outline">
    <div className="relative w-64 h-64 flex items-center justify-center">
      <FilteringOrbitAnimation />
    </div>
    <div className="space-y-2 max-w-lg px-4 mt-8">
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="text-lg font-medium"
      >
        2. Let AI filter and sync your work
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="text-sm text-muted-foreground"
      >
        Let AI separate the signal from the noise. It filters for the specific
        cases you want to automate and syncs them across your stack
      </motion.p>
    </div>
  </StepLayout>
);

export const HowItWorksStep3 = (props: HowItWorksStepProps) => (
  <StepLayout
    {...props}
    nextLabel="Get started"
    nextIcon={ArrowUpRight}
    centerButtons={true}
  >
    <div className="relative w-64 h-64 flex items-center justify-center">
      <WorkflowAnimation />
    </div>
    <div className="space-y-2 max-w-lg px-4 mt-8">
      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="text-lg font-medium"
      >
        3. Execute agentic workflows from your Inbox
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.4 }}
        className="text-sm text-muted-foreground"
      >
        Agents work directly from your inbox to autonomously validate invoices
        or schedule bookings, handing off only exceptions for human review
      </motion.p>
    </div>
  </StepLayout>
);
